import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Typeahead product filter + Tab/Arrow navigation for POS.
 *
 * - Letter/number/space → builds filter string
 * - Backspace → removes last char
 * - Escape → clears filter completely
 * - Tab / Shift+Tab → cycle through visible products
 * - ArrowDown / ArrowUp → cycle through visible products
 * - Enter → select highlighted product (or first visible if none highlighted)
 * - F1 = En Comedor, F2 = Para Recoger, F3 = A Domicilio, F4 = Plataformas
 * - F5 = Make order (Guardar) — triggers order with current delivery type
 */
export const usePosFilter = ({ onSelect, onDelivery, onMakeOrder } = {}) => {
  const [filter, setFilter] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const visibleCountRef = useRef(0);
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;
  const onSelectRef = useRef(onSelect);
  const onDeliveryRef = useRef(onDelivery);
  const onMakeOrderRef = useRef(onMakeOrder);

  // Keep refs fresh without re-attaching listener
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);
  useEffect(() => {
    onDeliveryRef.current = onDelivery;
  }, [onDelivery]);
  useEffect(() => {
    onMakeOrderRef.current = onMakeOrder;
  }, [onMakeOrder]);

  const setVisibleCount = useCallback((count) => {
    visibleCountRef.current = count;
  }, []);

  const clearFilter = useCallback(() => {
    setFilter("");
    setSelectedIndex(-1);
  }, []);

  useEffect(() => {
    const deliveryMap = {
      F1: "for_here",
      F2: "pickup",
      F3: "delivery",
      F4: "other_apps",
    };

    function onKeyDown(e) {
      // Skip when a modal is open (Reactstrap adds modal-open to body)
      if (document.body.classList.contains("modal-open")) return;

      const tag = e.target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        e.target.isContentEditable
      ) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Delivery shortcuts: F1-F4
      if (deliveryMap[e.key]) {
        e.preventDefault();
        if (onDeliveryRef.current) {
          onDeliveryRef.current(deliveryMap[e.key]);
        }
        return;
      }

      // F5 — Make order / Guardar
      if (e.key === "F5") {
        e.preventDefault();
        if (onMakeOrderRef.current) {
          onMakeOrderRef.current();
        }
        return;
      }

      // Escape — clear filter completely
      if (e.key === "Escape") {
        e.preventDefault();
        clearFilter();
        return;
      }

      if (e.key === "Backspace") {
        e.preventDefault();
        setFilter((prev) => prev.slice(0, -1));
        setSelectedIndex(-1);
        return;
      }

      // Tab / Shift+Tab / ArrowDown / ArrowUp — cycle through visible products
      if (e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const count = visibleCountRef.current;
        if (count === 0) return;
        const goBack = e.shiftKey || e.key === "ArrowUp";
        setSelectedIndex((prev) => {
          if (goBack) {
            return prev <= 0 ? count - 1 : prev - 1;
          }
          return prev >= count - 1 ? 0 : prev + 1;
        });
        return;
      }

      // Enter — select highlighted product (or first visible if none highlighted)
      if (e.key === "Enter") {
        e.preventDefault();
        let idx = selectedIndexRef.current;
        if (idx < 0 && visibleCountRef.current > 0) {
          idx = 0;
          setSelectedIndex(0);
          selectedIndexRef.current = 0;
        }
        if (onSelectRef.current) {
          onSelectRef.current(idx);
        }
        clearFilter();
        return;
      }

      // Accept letters, numbers, and space
      if (e.key.length === 1 && /[a-zA-Z0-9\s]/.test(e.key)) {
        e.preventDefault();
        setFilter((prev) => prev + e.key);
        setSelectedIndex(-1);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [clearFilter]);

  return { filter, clearFilter, selectedIndex, setVisibleCount };
};
